import { t } from '@lingui/macro';
import isPlainObject from 'lodash/isPlainObject';
import trim from 'lodash/trim';

// Turns the trigger payload schemas of an integration into pickable trees of
// dot-paths. A source may carry a JSON Schema (`properties`) or only an
// `example`; the example is used to infer the tree when the schema is absent.

const joinPath = (prefix, key) => (prefix ? `${prefix}.${key}` : key);

const typeOfValue = (value) => {
  if (Array.isArray(value)) return 'array';
  if (value === null) return 'null';
  return typeof value;
};

const isPrimitive = (value) => !isPlainObject(value) && !Array.isArray(value);

export const nodesFromExample = (example, prefix = '') => {
  if (!isPlainObject(example)) return [];

  return Object.entries(example).map(([key, value]) => {
    const path = joinPath(prefix, key);
    const type = typeOfValue(value);

    let children = [];
    if (isPlainObject(value)) children = nodesFromExample(value, path);
    else if (Array.isArray(value) && isPlainObject(value[0]))
      children = nodesFromExample(value[0], path);

    return {
      key,
      path,
      type,
      required: false,
      example: isPrimitive(value) ? value : undefined,
      children,
    };
  });
};

const schemaType = (property) => {
  if (property.type) {
    return Array.isArray(property.type)
      ? property.type.join(' | ')
      : property.type;
  }
  if (property.properties) return 'object';
  if (property.items) return 'array';
  return 'any';
};

const schemaExample = (property) => {
  if (property.example !== undefined) return property.example;
  if (Array.isArray(property.examples)) return property.examples[0];
  return property.default;
};

export const nodesFromSchema = (schema, prefix = '') => {
  if (!isPlainObject(schema?.properties)) return [];
  const required = Array.isArray(schema.required) ? schema.required : [];

  return Object.entries(schema.properties).map(([key, property]) => {
    const path = joinPath(prefix, key);
    const prop = isPlainObject(property) ? property : {};

    let children = [];
    if (prop.properties) children = nodesFromSchema(prop, path);
    else if (isPlainObject(prop.items) && prop.items.properties)
      children = nodesFromSchema(prop.items, path);

    const example = schemaExample(prop);

    return {
      key,
      path,
      type: schemaType(prop),
      required: required.includes(key),
      example: isPrimitive(example) ? example : undefined,
      children,
    };
  });
};

const dig = (object, keys) =>
  keys.reduce(
    (current, key) => (isPlainObject(current) ? current[key] : undefined),
    object,
  );

const firstObject = (value) => {
  if (isPlainObject(value)) return value;
  if (Array.isArray(value) && isPlainObject(value[0])) return value[0];
  return undefined;
};

// Nodes for the part of a source a condition is evaluated against: the
// sub-schema at `schemaPath`, or the matching slice of the example when the
// source only ships an example. `keep` narrows the result to one root key.
const nodesFor = (source, { schemaPath, examplePath, keep }) => {
  if (!isPlainObject(source)) return [];

  const schema = isPlainObject(source.schema) ? source.schema : source;
  const sub = dig(schema, schemaPath);
  let nodes = [];

  if (isPlainObject(sub?.properties)) {
    nodes = nodesFromSchema(sub);
  } else {
    const example =
      dig(firstObject(schema.example ?? source.example), examplePath) ??
      sub?.example;
    nodes = nodesFromExample(firstObject(example));
  }

  return keep ? nodes.filter((node) => node.key === keep) : nodes;
};

// Mirrors the API: the condition path is resolved against `data` for
// application events (a leading `data.` is accepted) and against
// `invocation_parameters` for HTTP requests (root-level paths).
const EVENT_SOURCE = { schemaPath: [], examplePath: [], keep: 'data' };

const HTTP_SOURCE = {
  schemaPath: [
    'properties',
    'data',
    'properties',
    'attributes',
    'properties',
    'invocation_parameters',
  ],
  examplePath: ['data', 'attributes', 'invocation_parameters'],
};

export const eventNodes = (source) => nodesFor(source, EVENT_SOURCE);

export const httpNodes = (source) => nodesFor(source, HTTP_SOURCE);

const eventLabel = (event, index) =>
  event?.name ||
  event?.title ||
  event?.schema?.title ||
  t`Application event ${index + 1}`;

// One entry per trigger source that yields at least one attribute.
export const buildPayloadSources = (schemas) => {
  const events = (schemas?.application_events || []).map((event, index) => ({
    id: `event-${index}`,
    label: eventLabel(event, index),
    nodes: eventNodes(event),
  }));

  const http = httpNodes(schemas?.http_api_request);
  const httpSource = http.length
    ? [{ id: 'http', label: t`HTTP API request`, nodes: http }]
    : [];

  return [...events.filter((source) => source.nodes.length), ...httpSource];
};

// Keeps every node whose path contains the query plus the ancestors needed to
// reach it. A matching node keeps its whole subtree.
export const filterTree = (nodes, query) => {
  const needle = trim(query || '').toLowerCase();
  if (!needle) return nodes;

  return nodes.flatMap((node) => {
    const hit = node.path.toLowerCase().includes(needle);
    const children = filterTree(node.children, needle);
    if (!hit && children.length === 0) return [];
    return [{ ...node, children: hit ? node.children : children }];
  });
};

// Locates a dot-path in a tree, returning the node and the paths of every
// ancestor, so a picker can expand and highlight an existing value.
export const findNode = (nodes, path, ancestors = []) => {
  if (!path) return null;

  return nodes.reduce((found, node) => {
    if (found) return found;
    if (node.path === path) return { node, ancestors };
    if (!path.startsWith(`${node.path}.`)) return null;
    return findNode(node.children, path, [...ancestors, node.path]);
  }, null);
};
